import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/store/auth/auth.store';
import { deleteInvite } from '@/store/invites/invites.api';
import { useInvalidateSubscriptions } from '@/store/subscriptions/subscriptions.store';
import type { UserRole } from '@/utils/roles';

import {
  getOwnershipInvites,
  leaveOwnership,
  promoteToCoOwner,
  removeCoOwner,
  transferOwnership,
} from './ownership.api';

const OWNERSHIP_KEY = 'ownership';

/** Every pending owner-group invitation on one vessel; a role narrows it to one list. */
export const ownershipInvitesKey = (vesselId: string | undefined, role?: string) =>
  [OWNERSHIP_KEY, 'invites', vesselId, ...(role ? [role] : [])] as const;

/**
 * Pending invitations to the owner group on one vessel, for one role: co-owner ones for the
 * owner and co-owners, the owner one for the captain of an ownerless vessel.
 */
export const useOwnershipInvitesQuery = (
  vesselId: string | undefined,
  role: UserRole.OWNER | UserRole.CO_OWNER,
  enabled = true,
) =>
  useQuery({
    queryKey: ownershipInvitesKey(vesselId, role),
    queryFn: () => getOwnershipInvites(vesselId!, role),
    enabled: enabled && !!vesselId,
  });

/**
 * What every ownership change leaves stale: the pending invitations (here and on My Yachts'
 * invitation tiles), the plan (`can_take_over` and the payer move with the owner), and the
 * viewer's own session — a transfer or a leave changes their role and rights on the vessel.
 * The crew roster needs nothing: it never lists the owner, co-owners or their invitations.
 */
const useAfterOwnershipChange = () => {
  const queryClient = useQueryClient();
  const invalidateSubscriptions = useInvalidateSubscriptions();
  const refreshSession = useAuth((state) => state.me);

  return async (vesselId: string, { session = false } = {}) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ownershipInvitesKey(vesselId) }),
      queryClient.invalidateQueries({ queryKey: ['invites-list'] }),
    ]);
    invalidateSubscriptions();
    if (session) await refreshSession();
  };
};

export const useRevokeCoOwnerInviteMutation = (vesselId: string) => {
  const after = useAfterOwnershipChange();
  return useMutation({
    mutationFn: (inviteId: string) => deleteInvite(inviteId),
    onSuccess: () => after(vesselId),
  });
};

export const useTransferOwnershipMutation = (vesselId: string) => {
  const after = useAfterOwnershipChange();
  return useMutation({
    mutationFn: (personId: string) => transferOwnership(vesselId, personId),
    onSuccess: () => after(vesselId, { session: true }),
  });
};

export const useLeaveOwnershipMutation = (vesselId: string) => {
  const after = useAfterOwnershipChange();
  return useMutation({
    mutationFn: (successorId?: string) => leaveOwnership(vesselId, successorId),
    onSuccess: () => after(vesselId, { session: true }),
  });
};

export const useRemoveCoOwnerMutation = (vesselId: string) => {
  const after = useAfterOwnershipChange();
  return useMutation({
    mutationFn: (personId: string) => removeCoOwner(vesselId, personId),
    onSuccess: () => after(vesselId),
  });
};

export const usePromoteToCoOwnerMutation = (vesselId: string) => {
  const after = useAfterOwnershipChange();
  return useMutation({
    mutationFn: (personId: string) => promoteToCoOwner(vesselId, personId),
    onSuccess: () => after(vesselId),
  });
};
